// src/companies/kontoSync.js — „Контрагентите от Конто" за всички фирми
// наведнъж (ПРОМТ-23). Само за админ, в „Мои фирми".
//
// Пътят е като при вноса на артикули: файл → преглед (нищо не се пише) →
// изричен „Внеси". Прегледът казва с едно изречение какво ще стане и показва
// таблица по фирма; подробностите (различните шифри, двойните кодове в Конто,
// новите контакти един по един) са в отчета — Excel, защото са хиляди редове.
// Логиката е на сървъра (services/kontoSync.js), тук е само екранът.

import { el, clear } from '../shared/dom.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { openModal, closeModal } from '../shared/modal.js';
import { state } from '../shared/state.js';
import { uploadRaw, dropZone } from '../shared/upload.js';
import { withXlsx } from '../shared/xlsx.js';

const n = (x) => Number(x || 0).toLocaleString('bg-BG');
const plural = (k, one, many) => n(k) + ' ' + (Number(k) === 1 ? one : many);
const FIELD = { acc_code: 'Счетоводен шифър', supplier_code: 'Шифър като доставчик' };
const ROLE = { customer: 'клиент', supplier: 'доставчик' };

// Изречението отгоре: какво ще стане (или стана), без цифри, които са нула.
export function leadText(r) {
  const t = r.totals || {};
  const will = !r.done;
  const parts = [];
  if (t.new_firms) parts.push(plural(t.new_firms, 'нова фирма', 'нови фирми'));
  if (t.new_contacts) parts.push(plural(t.new_contacts, 'нов контакт', 'нови контакта') + (t.persons ? ' (от тях ' + n(t.persons) + ' физически лица)' : ''));
  const codes = (t.filled || 0) + (t.fixed || 0) + (t.replaced || 0);
  let s = parts.length ? (will ? 'Ще се създадат ' : 'Създадени са ') + parts.join(' и ') + '.' : (will ? 'Няма нови фирми и контакти.' : 'Не са създадени нови фирми и контакти.');
  if (codes) s += ' ' + (will ? 'Ще се попълнят ' : 'Попълнени са ') + plural(codes, 'шифър', 'шифъра') + ' на заварени контакти.';
  if (t.data) s += ' ' + plural(t.data, 'контакт получава', 'контакта получават') + ' липсващи данни (град, адрес, МОЛ…).';
  const left = (t.conflicts || 0) - (t.replaced || 0);
  if (left > 0) s += ' ' + plural(left, 'шифър е различен', 'шифъра са различни') + ' от Конто и остава' + (left === 1 ? '' : 'т') + ' както е — вижте отчета.';
  if (t.name_mismatch) s += ' ' + plural(t.name_mismatch, 'контакт в CRM има ЕИК', 'контакта в CRM имат ЕИК') + ', който в Конто е на фирма с друго име — не се пипа' + (t.name_mismatch === 1 ? '' : 'т') + ' (лист „ЕИК с друго име“ в отчета).';
  if (t.ambiguous) s += ' В Конто ' + plural(t.ambiguous, 'ЕИК е', 'ЕИК-а са') + ' на две или повече различни фирми — контакт се прави само за най-новото име (лист „Един ЕИК — различни фирми“).';
  return s;
}

// Отчетът: лист по вид. Празен лист не се слага.
export function reportSheets(r) {
  const sheets = [];
  const firms = r.firms || [];
  sheets.push(['Фирми', firms.map((f) => ({
    'Фирма': f.name, 'ЕИК': f.firm_eik, 'Нова фирма': f.new_firm ? 'да' : '',
    'Нови контакти': f.create_count, 'Попълнени шифри': f.counts.filled, 'Поправени шифри': f.counts.fixed,
    'Различни шифри': f.conflicts.length, 'Допълнени данни': f.counts.data,
    ['Без движение от ' + String(r.cutoff || '').slice(0, 4)]: f.counts.old, 'Без ЕИК и ДДС №': f.counts.no_key,
    ['Кодове клиенти (' + f.customer_nom + ')']: f.known.customer, ['Кодове доставчици (' + (f.supplier_nom || '—') + ')']: f.known.supplier,
  }))]);
  const conf = [];
  firms.forEach((f) => f.conflicts.forEach((c) => conf.push({ 'Фирма': f.name, 'Контакт': c.name, 'Поле': FIELD[c.field] || c.field, 'В CRM': c.crm, 'В Конто': c.konto })));
  if (conf.length) sheets.push(['Различни шифри', conf]);
  const dup = [];
  firms.forEach((f) => f.dup_konto.forEach((d) => dup.push({ 'Фирма': f.name, 'Контрагент': d.name, 'ЕИК/ДДС': d.key, 'Като': ROLE[d.role] || d.role, 'Взет код': d.kept, 'Други кодове': d.others.join(', ') })));
  if (dup.length) sheets.push(['Двойни в Конто', dup]);
  const dupCrm = [];
  firms.forEach((f) => f.dup_crm.forEach((d) => dupCrm.push({ 'Фирма': f.name, 'ЕИК/ДДС': d.key, 'Контакти в CRM': d.names.join(' | ') })));
  if (dupCrm.length) sheets.push(['Двойни в CRM', dupCrm]);
  const mism = [];
  firms.forEach((f) => (f.name_mismatch || []).forEach((m) => mism.push({ 'Фирма': f.name, 'Контакт в CRM': m.name, 'ЕИК в CRM': m.eik, 'Името в Конто': m.konto_names.join(' | ') })));
  if (mism.length) sheets.push(['ЕИК с друго име', mism]);
  const amb = [];
  firms.forEach((f) => (f.ambiguous || []).forEach((a) => a.clusters.forEach((c, i) => amb.push({
    'Фирма': f.name, 'ЕИК/ДДС': a.key, 'Име в Конто': c.names.join(' | '), 'Код клиент': c.customer, 'Код доставчик': c.supplier,
    'Последно движение': c.last, 'Става контакт': i === 0 ? 'да (най-новото)' : 'не',
  }))));
  if (amb.length) sheets.push(['Един ЕИК — различни фирми', amb]);
  const created = [];
  firms.forEach((f) => f.create.forEach(([name, eik, acc, sup, type, last]) => created.push({
    'Фирма': f.name, 'Контакт': name, 'ЕИК/ДДС': eik, 'Счетоводен шифър': acc, 'Шифър като доставчик': sup,
    'Вид': type === 'supplier' ? 'Доставчик' : 'Клиент', 'Последно движение': last,
  })));
  if (created.length) sheets.push(['Нови контакти', created]);
  if ((r.crm_only || []).length) sheets.push(['Няма ги в Конто', r.crm_only.map((c) => ({ 'Фирма в CRM': c.name, 'ЕИК': c.eik }))]);
  return sheets;
}

function downloadReport(r) {
  if (typeof XLSX === 'undefined') { withXlsx(() => downloadReport(r)).catch(() => {}); return; }
  const wb = XLSX.utils.book_new();
  for (const [name, rows] of reportSheets(r)) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), name);
  XLSX.writeFile(wb, (r.done ? 'Конто-внос-отчет' : 'Конто-внос-преглед') + '.xlsx');
}

// file (по избор): вече избраният файл — от Контакти → Импорт, когато там е
// пусната тази таблица. Прегледът тръгва веднага.
export function kontoSyncDialog(preset) {
  let file = null;
  const out = el('div', { class: 'imp-out', 'aria-live': 'polite' });
  const foot = el('div', {});
  const replace = el('input', { type: 'checkbox' });
  const skipPersons = el('input', { type: 'checkbox' });
  const url = (mode) => '/api/companies/konto-sync?mode=' + mode + (replace.checked ? '&replace=1' : '') + (skipPersons.checked ? '&skip_persons=1' : '');

  function closeBtn(text) { return el('button', { class: 'btn ghost', type: 'button', text: text || 'Отказ', onClick: closeModal }); }
  function showError(text) {
    clear(out); clear(foot);
    out.appendChild(el('p', { class: 'sev-warn', role: 'alert', text }));
    foot.appendChild(closeBtn('Затвори'));
  }

  async function preview() {
    if (!file) return;
    clear(out); clear(foot);
    out.appendChild(el('p', { class: 'muted', text: 'Чета таблицата — около 10 секунди…' }));
    let r;
    try { r = await uploadRaw(url('preview'), file, { contentType: 'application/octet-stream' }); }
    catch (e) { showError(e.message); return; }
    render(r);
  }

  function render(r) {
    clear(out); clear(foot);
    out.appendChild(el('p', { class: 'imp-lead', text: leadText(r) }));
    const firms = (r.firms || []).filter((f) => f.new_firm || f.create_count || f.update_count || f.conflicts.length);
    if (firms.length) {
      const tb = el('tbody', {}, ...firms.map((f) => el('tr', {},
        el('td', {}, el('span', { text: f.name }), f.new_firm ? el('span', { class: 'tag blue', style: 'margin-left:6px', text: 'нова' }) : null),
        el('td', { class: 'num', text: n(f.create_count) }),
        el('td', { class: 'num', text: n(f.counts.filled + f.counts.fixed + f.counts.replaced) }),
        el('td', { class: 'num', text: f.conflicts.length - f.counts.replaced > 0 ? n(f.conflicts.length - f.counts.replaced) : '' }))));
      out.appendChild(el('div', { class: 'table-wrap', style: 'max-height:320px;overflow:auto' }, el('table', {},
        el('thead', {}, el('tr', {},
          el('th', { scope: 'col', text: 'Фирма' }), el('th', { scope: 'col', class: 'num', text: 'Нови контакти' }),
          el('th', { scope: 'col', class: 'num', text: 'Шифри' }), el('th', { scope: 'col', class: 'num', text: 'Различни' }))), tb)));
    }
    if ((r.crm_only || []).length) {
      out.appendChild(el('p', { class: 'muted', text: 'Няма ги в таблицата (не се пипат): ' + r.crm_only.map((c) => c.name).join(', ') + '.' }));
    }
    foot.appendChild(el('button', { class: 'btn ghost', type: 'button', text: 'Свали отчета (Excel)', onClick: () => downloadReport(r) }));
    if (r.done) {
      foot.appendChild(el('button', { class: 'btn', type: 'button', text: 'Готово', style: 'margin-left:8px', onClick: async () => {
        closeModal();
        try { state().companies = await api('/companies'); } catch (e) { /* остава старият списък */ }
        window.CRM.renderShell();
      } }));
      return;
    }
    foot.appendChild(closeBtn());
    const t = r.totals || {};
    const any = t.new_firms || t.new_contacts || (t.filled + t.fixed + t.replaced) || t.data;
    if (!any) return;
    const go = el('button', { class: 'btn', type: 'button', text: 'Внеси', style: 'margin-left:8px', onClick: async () => {
      go.disabled = true;
      go.textContent = 'Внасям…';
      let done;
      try { done = await uploadRaw(url('apply'), file, { contentType: 'application/octet-stream' }); }
      catch (e) { go.disabled = false; go.textContent = 'Внеси'; toast(e.message, true); return; }
      toast('Готово.');
      render(done);
    } });
    foot.appendChild(go);
  }

  [replace, skipPersons].forEach((c) => c.addEventListener('change', () => { if (file) preview(); }));
  const zone = dropZone({
    id: 'konto-sync-file', title: 'Таблицата с контрагентите от Конто', accept: '.xlsx,.xls',
    hint: 'Файлът от агента на Конто — всички фирми в една таблица. Нищо не се записва, преди да натиснете „Внеси“.',
    onFiles: (files) => {
      const f = files && files[0];
      if (!f) return;
      if (!/\.xlsx?$/i.test(f.name)) { showError('Изберете Excel файл (.xlsx).'); return; }
      file = f;
      preview();
    },
  });
  const body = el('div', { class: 'imp-dialog' },
    el('p', { class: 'muted', text: 'Създава липсващите фирми, попълва шифрите на клиентите и доставчиците и добавя контрагентите с движение от 2024 г. насам. Попълненото в CRM-а не се сменя.' }),
    zone,
    el('label', { class: 'choice' }, replace, el('span', { text: 'Замени различните шифри с тези от Конто' })),
    el('label', { class: 'choice' }, skipPersons, el('span', { text: 'Без физическите лица (с ЕГН)' })),
    out);
  openModal({ title: 'Контрагентите от Конто', body, foot, wide: true });
  foot.appendChild(closeBtn());
  if (preset) { file = preset; preview(); }
}

// ---- „Аналитичности и номенклатури" (ПРОМТ-31) ---------------------------------
// Трите таблици на агента на Конто (сметки-аналитичности-*.xlsx с лист
// „Сметки", номенклатури-разходи-всички-фирми.xlsx с „Елементи" и
// „Сметки→Статии") — по една заявка на файл; прегледът казва за колко фирми
// има данни, после „Внеси" записва всичко. Оттам доставките към Конто
// знаят нивата на всяка сметка и статиите на всяка фирма.
export function kontoAnalyticsDialog() {
  let files = [];
  const out = el('div', { class: 'imp-out', 'aria-live': 'polite' });
  const foot = el('div', {});
  const url = (mode) => '/api/companies/konto-analytics?mode=' + mode;
  function closeBtn(text) { return el('button', { class: 'btn ghost', type: 'button', text: text || 'Отказ', onClick: closeModal }); }
  function showError(text) { clear(out); clear(foot); out.appendChild(el('p', { class: 'sev-warn', role: 'alert', text })); foot.appendChild(closeBtn('Затвори')); }

  async function run(mode) {
    clear(out); clear(foot);
    out.appendChild(el('p', { class: 'muted', text: (mode === 'apply' ? 'Записвам' : 'Чета таблиците') + ' — до минута при 60 хил. реда…' }));
    const results = [];
    for (const f of files) {
      try { results.push(Object.assign({ file: f.name }, await uploadRaw(url(mode), f, { contentType: 'application/octet-stream' }))); }
      catch (e) { showError(f.name + ': ' + e.message); return; }
    }
    render(results, mode === 'apply');
  }
  function render(results, done) {
    clear(out); clear(foot);
    const firms = new Map();
    const missing = new Map();
    for (const r of results) {
      for (const f of r.firms || []) {
        const x = firms.get(f.company_id) || { name: f.name, accounts: 0, noms: 0, elements: 0, articles: 0, ambiguous: [], mismatch: [] };
        x.accounts += f.accounts; x.noms += f.noms; x.elements += f.elements; x.articles += f.articles || 0;
        x.ambiguous = x.ambiguous.concat(f.ambiguous || []); x.mismatch = x.mismatch.concat(f.mismatch || []);
        firms.set(f.company_id, x);
      }
      for (const m of r.missing || []) missing.set(m.eik, m);
    }
    const list = [...firms.values()];
    const total = list.reduce((t, f) => ({ accounts: t.accounts + f.accounts, elements: t.elements + f.elements, articles: t.articles + f.articles }), { accounts: 0, elements: 0, articles: 0 });
    out.appendChild(el('p', { class: 'imp-lead', text: (done ? 'Записани са ' : 'Ще се запишат ') + plural(list.length, 'фирма', 'фирми') + ': ' + plural(total.accounts, 'сметка', 'сметки') + ' с нивата им и ' + plural(total.elements, 'елемент', 'елемента') + ' на номенклатури (' + results.map((r) => r.file + ' — ' + (r.sheets || []).join(', ')).join('; ') + ').' }));
    if (list.length) {
      out.appendChild(el('div', { class: 'table-wrap', style: 'max-height:320px;overflow:auto' }, el('table', {},
        el('thead', {}, el('tr', {}, el('th', { scope: 'col', text: 'Фирма' }), el('th', { scope: 'col', class: 'num', text: 'Сметки' }), el('th', { scope: 'col', class: 'num', text: 'Номенклатури' }), el('th', { scope: 'col', class: 'num', text: 'Елементи' }))),
        el('tbody', {}, ...list.map((f) => el('tr', {}, el('td', { text: f.name }), el('td', { class: 'num', text: n(f.accounts) }), el('td', { class: 'num', text: n(f.noms) }), el('td', { class: 'num', text: n(f.elements) })))))));
    }
    if (total.articles) out.appendChild(el('p', { text: (done ? 'Попълнен е ' : 'Ще се попълни ') + '„Код за Конто" на ' + plural(total.articles, 'артикул', 'артикула') + ' — по името им в Конто.' }));
    const amb = list.flatMap((f) => f.ambiguous.map((a) => f.name + ': „' + a.name + '" — ' + (a.articles > 1 ? 'няколко артикула с това име; ' : '') + 'в Конто: ' + a.codes.join(', ')));
    const mis = list.flatMap((f) => f.mismatch.map((m) => f.name + ': „' + m.name + '" има код ' + m.code + ', а в Конто той е „' + m.konto_name + '"'));
    if (amb.length || mis.length) {
      out.appendChild(el('details', { class: 'ks-more' }, el('summary', {}, el('span', { text: 'За решение' }), ' — не се пипат (' + (amb.length + mis.length) + ')'),
        el('ul', {}, ...amb.concat(mis).slice(0, 200).map((t) => el('li', { text: t }))),
        el('p', { class: 'muted', text: 'Изберете кода на ръка в артикула („За счетоводителя" → „Код за Конто"). Двойните не се попълват, за да не отиде стоката по чужд код.' })));
    }
    if (missing.size) out.appendChild(el('p', { class: 'muted', text: 'Няма ги в CRM-а (не се записват; първо „Внос от Конто" с контрагентите създава фирмите): ' + [...missing.values()].map((m) => m.name + ' (' + m.eik + ')').join(', ') + '.' }));
    if (done) {
      foot.appendChild(el('button', { class: 'btn', type: 'button', text: 'Готово', onClick: closeModal }));
      return;
    }
    foot.appendChild(closeBtn());
    if (list.length) foot.appendChild(el('button', { class: 'btn', type: 'button', text: 'Внеси', style: 'margin-left:8px', onClick: () => run('apply') }));
  }
  const zone = dropZone({
    id: 'konto-analytics-files', title: 'Таблиците с аналитичностите и номенклатурите от Конто', accept: '.xlsx,.xls', multiple: true,
    hint: 'Може наведнъж: „сметки-аналитичности-гр30-гр60.xlsx", „номенклатури-разходи-всички-фирми.xlsx" и старият „сметки-аналитичности-всички-фирми.xlsx". Нищо не се записва, преди да натиснете „Внеси".',
    onFiles: (fl) => {
      const picked = [...(fl || [])].filter((f) => /\.xlsx?$/i.test(f.name));
      if (!picked.length) { showError('Изберете Excel файл (.xlsx).'); return; }
      files = picked;
      run('preview');
    },
  });
  const body = el('div', { class: 'imp-dialog' },
    el('p', { class: 'muted', text: 'За всяка фирма: как е разбита всяка сметка в Конто (нивата на партидата) и кои елементи има всяка номенклатура — статиите на 601/602/609, видът дейност, мерките, артикулите. По тях доставките към Конто получават верни партиди. Новият внос заменя старите данни; статиите, дадени от CRM-а, остават.' }),
    zone, out);
  // ПРОМТ-45: основният внос е от JSON експорта (src/companies/kontoJson.js); това е старият.
  openModal({ title: 'Аналитичности и номенклатури от Конто — стар формат (xlsx)', body, foot, wide: true });
  foot.appendChild(closeBtn());
}
